import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Alert, Button, Input, Modal, Tooltip, message } from "antd";
import gql from "fraql";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isBlank from "../../../../utils/isBlank";
import isJSON from "../../../../utils/isJson";
import ColumnValueText from "../../../ColumnValueText";
import EmptyValueText from "../../../EmptyValueText";
import SpinPageContent from "../../../SpinPageContent";
import TableWithTruncation from "../../../TableWithTruncation/TableWithTruncation";
import TeamConditions from "./TeamConditions";

function getBackendErrorMessage(error, fallbackMessage) {
  const graphQlMessage = get(error, "graphQLErrors.0.message", "");

  if (isJSON(graphQlMessage)) {
    try {
      const parsedMessage = JSON.parse(graphQlMessage);
      const firstError = get(parsedMessage, "errors.0", null);

      const nonFieldErrorMessage = get(firstError, "non_field_errors.0.message", null);

      if (!isBlank(nonFieldErrorMessage)) {
        return nonFieldErrorMessage;
      }

      const fieldErrorMessage = get(firstError, "message", null);

      if (!isBlank(fieldErrorMessage)) {
        return fieldErrorMessage;
      }
    } catch (parseError) {
      // Fall back to the generic message below when backend payload is malformed.
    }
  }

  return get(error, "message", fallbackMessage);
}

const GET_REGISTRATION_INVITES_FOR_TEAM = gql`
  query GetRegistrationInvitesForTeam($TeamId: ID!) {
    getRegistrationInvitesForTeam(TeamId: $TeamId) {
      affected_rows
      RegistrationInvites {
        RegistrationInviteId
        TeamInviteId
        RegId
        SentToPersonId
        SentToEmail
        authCode
      }
    }
  }
`;

const CREATE_REGISTRATION_INVITES = gql`
  mutation CreateRegistrationInvites($RegistrationInvites: [CreateRegistrationInviteItemInput!]!) {
    createRegistrationInvites(RegistrationInvites: $RegistrationInvites) {
      affected_rows
      RegistrationInvites {
        RegistrationInviteId
        TeamInviteId
        authCode
      }
    }
  }
`;

const DELETE_TEAM_INVITE = gql`
  mutation DeleteTeamInvite($TeamId: ID!, $SentToEmail: String!) {
    deleteTeamInvite(TeamId: $TeamId, SentToEmail: $SentToEmail) {
      affected_rows
      deleted_registration_invites
    }
  }
`;

const NOTIFY_INVITED_TEAM_MEMBERS = gql`
  mutation NotifyInvitedTeamMembers($TeamId: ID!) {
    notifyInvitedTeamMembers(TeamId: $TeamId) {
      affected_rows
      failed_rows
      skipped_rows
    }
  }
`;

const INVITED_COLUMNS = [
  {
    title: "Email Address",
    key: "SentToEmail",
    dataIndex: "SentToEmail",
    render: value => {
      if (isEmpty(value)) {
        return <EmptyValueText />;
      }

      return (
        <Tooltip placement="topLeft" trigger="hover" title={value}>
          <ColumnValueText>{value}</ColumnValueText>
        </Tooltip>
      );
    },
    minWidth: 420,
  },
  {
    title: "Actions",
    key: "actions",
    render: (text, record) => {
      const sentToEmail = get(record, "SentToEmail", null);

      if (isEmpty(sentToEmail)) {
        return <EmptyValueText text="[No email]" />;
      }

      return <ColumnValueText>{sentToEmail}</ColumnValueText>;
    },
    width: 120,
  },
];

function ViewTeamDrawerInvitedTab({ teamId, regId, readOnly }) {
  const [email, setEmail] = useState("");
  const normalizedTeamId = useMemo(() => {
    return typeof teamId === "string" ? teamId.trim() : teamId;
  }, [teamId]);
  const hasTeamId = !isBlank(normalizedTeamId);

  const { loading: invitesLoading, error: invitesError, data: invitesData, refetch: refetchInvites } = useQuery(
    GET_REGISTRATION_INVITES_FOR_TEAM,
    {
      variables: { TeamId: normalizedTeamId },
      skip: !hasTeamId,
    },
  );

  const [createRegistrationInvites, { loading: createInviteLoading }] = useMutation(CREATE_REGISTRATION_INVITES);
  const [deleteTeamInvite, { loading: deleteInviteLoading }] = useMutation(DELETE_TEAM_INVITE);
  const [notifyInvitedTeamMembers, { loading: notifyInvitesLoading }] = useMutation(NOTIFY_INVITED_TEAM_MEMBERS);

  const loading = invitesLoading || createInviteLoading || deleteInviteLoading;

  const invitedDataSource = useMemo(() => {
    return get(invitesData, "getRegistrationInvitesForTeam.RegistrationInvites", []);
  }, [invitesData]);

  const hasInvitees = useMemo(() => {
    return invitedDataSource.length > 0;
  }, [invitedDataSource]);

  const isEmailValid = useMemo(() => {
    if (isEmpty(email)) {
      return false;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }, [email]);

  const handleChangeEmail = useCallback(event => {
    setEmail(event.target.value);
  }, []);

  const handleClickInvite = useCallback(async () => {
    const trimmedEmail = email.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      message.error("Please enter a valid email address.");
      return;
    }

    try {
      await createRegistrationInvites({
        variables: {
          RegistrationInvites: [
            {
              RegId: regId,
              TeamId: normalizedTeamId,
              SentToEmail: trimmedEmail,
            },
          ],
        },
      });

      setEmail("");
      message.success("Team invite created.");
      await refetchInvites();
    } catch (error) {
      message.error(getBackendErrorMessage(error, "Failed to create invite."));
    }
  }, [createRegistrationInvites, email, regId, normalizedTeamId, refetchInvites]);

  const handleDeleteInvite = useCallback(
    record => {
      const sentToEmail = get(record, "SentToEmail", null);

      if (isEmpty(sentToEmail)) {
        message.error("This invite cannot be deleted because no email was found.");
        return;
      }

      Modal.confirm({
        title: "Delete invite",
        content: `Are you sure you want to delete the invite for ${sentToEmail}?`,
        okText: "Delete",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteTeamInvite({
              variables: {
                TeamId: normalizedTeamId,
                SentToEmail: sentToEmail,
              },
            });

            message.success("Team invite deleted.");
            await refetchInvites();
          } catch (error) {
            message.error(getBackendErrorMessage(error, "Failed to delete invite."));
          }
        },
      });
    },
    [deleteTeamInvite, normalizedTeamId, refetchInvites],
  );

  const handleClickSendInvitesByEmail = useCallback(() => {
    Modal.confirm({
      title: "Send invitation emails",
      content: "Are you sure you want to send invitation emails to invited team members?",
      okText: "Send",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await notifyInvitedTeamMembers({
            variables: {
              TeamId: normalizedTeamId,
            },
          });

          const affectedRows = get(response, "data.notifyInvitedTeamMembers.affected_rows", 0);
          const failedRows = get(response, "data.notifyInvitedTeamMembers.failed_rows", 0);
          const skippedRows = get(response, "data.notifyInvitedTeamMembers.skipped_rows", 0);

          message.success(`Sent ${affectedRows} invitations. ${failedRows} failed, ${skippedRows} skipped.`);
          await refetchInvites();
        } catch (error) {
          message.error(getBackendErrorMessage(error, "Failed to send invitation emails."));
        }
      },
    });
  }, [notifyInvitedTeamMembers, normalizedTeamId, refetchInvites]);

  const invitedColumns = useMemo(() => {
    const columns = [...INVITED_COLUMNS];

    columns[1] = {
      ...columns[1],
      render: (text, record) => {
        const sentToEmail = get(record, "SentToEmail", null);

        if (isEmpty(sentToEmail)) {
          return <EmptyValueText text="[No email]" />;
        }

        return (
          <Button icon="delete" size="small" onClick={() => handleDeleteInvite(record)} disabled={deleteInviteLoading}>
            Delete
          </Button>
        );
      },
    };

    return columns;
  }, [handleDeleteInvite, deleteInviteLoading]);

  if (invitesLoading && !invitesData) {
    return <SpinPageContent />;
  }

  if (invitesError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Invites failed to load"
          description="Sorry, there was an issue loading invites for this team."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      <TeamConditions teamId={teamId} regId={regId} />

      {!readOnly && (
        <div>
          <div>Add email addresses to invite your team members</div>
          <div style={{ display: "flex", gap: 8, padding: "8px 0 16px 0" }}>
            <Input
              placeholder="Enter invitee email address"
              value={email}
              onChange={handleChangeEmail}
              maxLength={255}
              disabled={loading}
            />
            <Button
              type="primary"
              onClick={handleClickInvite}
              loading={createInviteLoading}
              disabled={!isEmailValid || isNil(regId)}
            >
              Invite
            </Button>
          </div>
        </div>
      )}

      <TableWithTruncation
        columns={invitedColumns}
        includeIndexColumn
        dataSource={invitedDataSource}
        loading={loading}
        rowKey="TeamInviteId"
        size="small"
        pagination={false}
        bordered={false}
        scroll={{ y: 54 * 10 }}
        locale={{
          emptyText: "No invited team members",
        }}
      />

      <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16 }}>
        <Button
          type="primary"
          onClick={handleClickSendInvitesByEmail}
          loading={notifyInvitesLoading}
          disabled={!hasInvitees}
        >
          Send Invites by Email
        </Button>
      </div>
    </div>
  );
}

ViewTeamDrawerInvitedTab.propTypes = {
  teamId: PropTypes.string,
  regId: PropTypes.string,
  readOnly: PropTypes.bool,
};

ViewTeamDrawerInvitedTab.defaultProps = {
  teamId: undefined,
  regId: undefined,
  readOnly: false,
};

export default ViewTeamDrawerInvitedTab;
